import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Plus, Edit, Trash2, Image as ImageIcon, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const CMSRealisations = () => {
  const { user } = useAuth();
  const [realisations, setRealisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  
  const [formData, setFormData] = useState({
    id: '',
    reference: '',
    titre: '',
    secteur: 'Infrastructure & Voirie',
    date_realisation: '',
    lieu: '',
    statut: 'En cours',
    description: '',
    image: ''
  });

  useEffect(() => {
    fetchRealisations();
  }, []);

  const fetchRealisations = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/realisations`);
      setRealisations(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des réalisations');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = (realisation) => {
    setFormData({
      id: realisation.id,
      reference: realisation.reference,
      titre: realisation.titre,
      secteur: realisation.secteur,
      date_realisation: realisation.date_realisation,
      lieu: realisation.lieu,
      statut: realisation.statut,
      description: realisation.description,
      image: realisation.image
    });
    setFile(null);
    setIsEditing(true);
  };

  const handleNew = () => {
    setFormData({
      id: '',
      reference: '',
      titre: '',
      secteur: 'Infrastructure & Voirie',
      date_realisation: '',
      lieu: '',
      statut: 'En cours',
      description: '',
      image: ''
    });
    setFile(null);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Voulez-vous vraiment supprimer cette réalisation ?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/realisations/${id}`, { withCredentials: true });
        toast.success('Réalisation supprimée');
        fetchRealisations();
      } catch (e) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let imagePath = formData.image;
      
      if (file) {
        const uploadData = new FormData();
        uploadData.append('file', file);
        const uploadRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, uploadData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imagePath = uploadRes.data.path;
      }

      const payload = { ...formData, image: imagePath };
      
      if (formData.id) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/realisations/${formData.id}`, payload, { withCredentials: true });
        toast.success('Réalisation mise à jour');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/realisations`, payload, { withCredentials: true });
        toast.success('Réalisation ajoutée');
      }
      
      setIsEditing(false);
      fetchRealisations();
    } catch (e) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0A192F]">Gestion des Réalisations (CMS)</h1>
          <p className="text-slate-500 font-medium">Administration des projets publiés</p>
        </div>
        {!isEditing && (
          <Button onClick={handleNew} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="ajouter-projet-btn">
            <Plus className="w-4 h-4 mr-2" /> Ajouter un projet
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-8">
          <div className="p-5 border-b border-slate-200 bg-slate-50">
            <h2 className="text-lg font-bold text-slate-900">{formData.id ? 'Modifier la réalisation' : 'Nouvelle réalisation'}</h2>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="realisation-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Référence *</label>
                <input required type="text" name="reference" value={formData.reference} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" placeholder="Ex: A10" data-testid="input-reference" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Titre du projet *</label>
                <input required type="text" name="titre" value={formData.titre} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" data-testid="input-titre" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Secteur *</label>
                <select required name="secteur" value={formData.secteur} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" data-testid="select-secteur">
                  <option value="Infrastructure & Voirie">Infrastructure & Voirie</option>
                  <option value="Infrastructure & Transport">Infrastructure & Transport</option>
                  <option value="Éducation">Éducation</option>
                  <option value="Santé">Santé</option>
                  <option value="Social & Protection">Social & Protection</option>
                  <option value="Sécurité">Sécurité</option>
                  <option value="Bâtiments Publics">Bâtiments Publics</option>
                  <option value="Eau & Énergie">Eau & Énergie</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Statut *</label>
                <select required name="statut" value={formData.statut} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" data-testid="select-statut">
                  <option value="Inauguré">Inauguré</option>
                  <option value="Achevé">Achevé</option>
                  <option value="En cours">En cours</option>
                  <option value="En projet">En projet</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Date ou Période *</label>
                <input required type="text" name="date_realisation" value={formData.date_realisation} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" placeholder="Ex: Juin 2025" data-testid="input-date" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Lieu *</label>
                <input required type="text" name="lieu" value={formData.lieu} onChange={handleChange} className="w-full px-4 py-2 border border-slate-300 rounded-md" placeholder="Ex: Kolwezi" data-testid="input-lieu" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-slate-700 mb-1">Description détaillée *</label>
                <textarea required name="description" value={formData.description} onChange={handleChange} rows={4} className="w-full px-4 py-2 border border-slate-300 rounded-md resize-none" data-testid="input-description" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Image illustratoire</label>
                <div className="flex items-center gap-4">
                  <div className="flex-grow">
                    <input type="file" accept="image/*" onChange={handleFileChange} className="w-full px-4 py-2 border border-slate-300 rounded-md bg-slate-50" />
                  </div>
                  {(formData.image || file) && (
                    <div className="w-16 h-16 bg-slate-100 rounded border border-slate-300 overflow-hidden flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">Laissez vide pour conserver l'image actuelle (lors d'une modification).</p>
                {formData.image && (
                  <p className="text-xs font-mono text-slate-400 mt-1 truncate">
                    Image actuelle: {formData.image}
                  </p>
                )}
              </div>
            </div>
            <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>Annuler</Button>
              <Button type="submit" disabled={isSubmitting} className="bg-blue-900 text-white" data-testid="realisation-form-submit">
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
             <div className="p-12 text-center flex items-center justify-center">
               <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
             </div>
          ) : realisations.length === 0 ? (
            <div className="p-12 text-center">
              <h3 className="text-lg font-bold text-slate-800 mb-2">Aucune réalisation</h3>
              <p className="text-slate-500">Commencez par ajouter un premier projet au catalogue.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
                    <th className="px-6 py-4">Réf</th>
                    <th className="px-6 py-4">Projet</th>
                    <th className="px-6 py-4">Secteur / Lieu</th>
                    <th className="px-6 py-4">Statut</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {realisations.map(realisation => (
                    <tr key={realisation.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-mono text-sm font-bold text-blue-900">{realisation.reference}</td>
                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-900 text-sm mb-1">{realisation.titre}</div>
                        <div className="text-xs text-slate-500">{realisation.date_realisation}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium">{realisation.secteur}</div>
                        <div className="text-xs text-slate-500">{realisation.lieu}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${
                          realisation.statut === 'Inauguré' || realisation.statut === 'Achevé'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {realisation.statut}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(realisation)} className="h-8 w-8 text-blue-600 hover:bg-blue-50" data-testid={`realisation-edit-btn-${realisation.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(realisation.id)} className="h-8 w-8 text-red-600 hover:bg-red-50" data-testid={`realisation-delete-btn-${realisation.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CMSRealisations;
